import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Select,
  cx,
  formatMoney,
} from "../components/ui";

interface SellablePlan {
  id: string;
  name: string;
  description: string | null;
  features: string[];
  productName: string;
  productCode: string;
  currency: string;
  buildCents: number;
  monthlyCents: number | null;
  yearlyCents: number | null;
  deliveredOnPurchase: boolean;
}

/**
 * The shelf, inside the portal.
 *
 * A customer who has bought once should not have to go back to the marketing
 * site and retype everything to buy again — we already know who they are, so
 * this is two clicks and a card.
 */
export function Plans() {
  const plans = useQuery({
    queryKey: ["sellable-plans"],
    queryFn: () => api<SellablePlan[]>("/api/v1/checkout/plans"),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Order something"
        lede="Everything we sell without needing to talk first. Anything else, get in touch."
      />

      {plans.isPending && <p className="text-sm text-dim">Loading…</p>}
      {plans.isError && <ErrorNote>Could not load what is available. Reload to try again.</ErrorNote>}

      {plans.isSuccess &&
        (plans.data.length === 0 ? (
          <Card>
            <EmptyState>
              Nothing is on sale here at the moment. Get in touch and we will quote the work.
            </EmptyState>
          </Card>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {plans.data.map((plan) => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
          </div>
        ))}
    </div>
  );
}

function PlanCard({ plan }: { plan: SellablePlan }) {
  // Monthly unless it is the only option. The smaller commitment is the honest
  // one to preselect.
  const [interval, setInterval] = useState<"month" | "year">(
    plan.monthlyCents === null ? "year" : "month",
  );
  const [brief, setBrief] = useState("");

  const recurring = interval === "year" ? plan.yearlyCents : plan.monthlyCents;

  const buy = useMutation({
    mutationFn: () =>
      api<{ url: string }>(`/api/v1/checkout/plans/${plan.id}/order`, {
        method: "POST",
        body: { interval, brief: brief.trim() || undefined },
      }),
    onSuccess: (result) => {
      // Leaving for Stripe. The order is already written, so coming back
      // without paying leaves something they can settle from Orders.
      window.location.assign(result.url);
    },
  });

  return (
    <Card
      title={plan.name}
      action={
        <Badge tone={plan.deliveredOnPurchase ? "live" : "accent"}>
          {plan.deliveredOnPurchase ? "Available at once" : "Built for you"}
        </Badge>
      }
    >
      <div className="space-y-4 p-4">
        {plan.description !== null && (
          <p className="max-w-prose text-sm text-muted">{plan.description}</p>
        )}

        {plan.features.length > 0 && (
          <ul className="space-y-1 text-sm text-muted">
            {plan.features.map((feature) => (
              <li key={feature} className="flex gap-2">
                <span aria-hidden className="text-accent">
                  →
                </span>
                {feature}
              </li>
            ))}
          </ul>
        )}

        <dl className="space-y-2 border-t border-hairline pt-4 text-sm">
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-dim">Build</dt>
            <dd className="tabular text-ink">{formatMoney(plan.buildCents, plan.currency)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-dim">Maintenance and hosting</dt>
            <dd className="tabular text-ink">
              {formatMoney(recurring, plan.currency)} / {interval === "year" ? "yr" : "mo"}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-4 border-t border-hairline pt-2">
            <dt className="text-ink">Due today</dt>
            <dd className={cx("tabular text-base font-semibold text-ink")}>
              {formatMoney(plan.buildCents + (recurring ?? 0), plan.currency)}
            </dd>
          </div>
        </dl>

        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            buy.mutate();
          }}
        >
          <label className="block space-y-1.5">
            <span className="field-label">Billed</span>
            <Select
              value={interval}
              onChange={(event) => setInterval(event.target.value as "month" | "year")}
            >
              {plan.monthlyCents !== null && (
                <option value="month">
                  Monthly — {formatMoney(plan.monthlyCents, plan.currency)}
                </option>
              )}
              {plan.yearlyCents !== null && (
                <option value="year">
                  Yearly — {formatMoney(plan.yearlyCents, plan.currency)}
                </option>
              )}
            </Select>
          </label>

          <label className="block space-y-1.5">
            <span className="field-label">What you need</span>
            <textarea
              value={brief}
              rows={3}
              maxLength={2000}
              placeholder="Anything we should know before we start."
              className="w-full rounded-md border border-hairline-strong bg-base px-3 py-2 text-sm text-ink placeholder:text-dim focus:border-accent"
              onChange={(event) => setBrief(event.target.value)}
            />
          </label>

          {buy.isError && <ErrorNote>{messageFor(buy.error)}</ErrorNote>}

          <Button
            type="submit"
            variant="primary"
            className="min-h-[2.75rem] w-full"
            disabled={buy.isPending}
          >
            {buy.isPending ? "Taking you to checkout…" : "Continue to payment"}
          </Button>
        </form>
      </div>
    </Card>
  );
}

function messageFor(error: unknown): string {
  return error instanceof ApiError ? error.message : "Something went wrong. Try again.";
}
